import { Fragment, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { DateRange } from "@mui/x-date-pickers-pro";
import {
    Alert,
    Box,
    Card,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import axiosInstance from "../AxiosInstance";
import { downloadCsv } from "../Utilities/csvUtility";
import CustomDatePicker from "./CustomDatePicker";
import EposTabs from "./EposTabs";
import EposPerformanceTrendChart from "./EposPerformanceTrendChart";
import ProductFilters from "./ProductFilters";
import ProductFiltersButton from "./ProductFiltersButton";

type Grain = "daily" | "weekly";
type ChartMode = "total" | "sku";
type WaitroseSkuMetric = "registeredSales" | "salesUnits" | "reductions" | "explainedWastage" | "explainedWastageQuality";
type WaitroseChartMetric = WaitroseSkuMetric | "none";

interface WaitrosePerformancePeriod {
    period: string;
    registeredSales: number;
    salesUnits: number;
    reductions: number;
    explainedWastage: number;
    explainedWastageQuality: number;
}

interface WaitrosePerformanceResponse {
    periods: WaitrosePerformancePeriod[];
}

interface WaitroseSkuPerformanceValue {
    period: string;
    value: number;
}

interface WaitroseSkuPerformanceItem {
    lineNumber: string;
    lineDescription: string;
    values: WaitroseSkuPerformanceValue[];
}

interface WaitroseSkuPerformanceResponse {
    items: WaitroseSkuPerformanceItem[];
    isTruncated?: boolean;
    resultLimit?: number;
}

interface WaitroseStorePerformance {
    branchName: string;
    registeredSales: number;
    salesUnits: number;
    reductions: number;
    explainedWastage: number;
    explainedWastageQuality: number;
}

interface WaitroseStorePerformancePage {
    items: WaitroseStorePerformance[];
    totalCount: number;
    page: number;
    pageSize: number;
}

interface ProductOption {
    productId: number;
    skuNumber: string;
    skuDescription: string;
    brand: string;
    groupName?: string | null;
}

interface ProductFilterParams {
    productIds?: number[];
    brands?: string[];
    productGroups?: string[];
}

type WaitroseStoreSortKey = "branchName" | "registeredSales" | "salesUnits" | "reductions" | "explainedWastage" | "explainedWastageQuality";

interface StoreColumnDefinition {
    key: WaitroseStoreSortKey;
    label: string;
    align?: "left" | "right";
}

const storeColumns: StoreColumnDefinition[] = [
    { key: "branchName", label: "Branch" },
    { key: "registeredSales", label: "Registered sales", align: "right" },
    { key: "salesUnits", label: "Sales units", align: "right" },
    { key: "reductions", label: "Reductions", align: "right" },
    { key: "explainedWastage", label: "Explained wastage", align: "right" },
    { key: "explainedWastageQuality", label: "Quality wastage", align: "right" }
];

const getDefaultWeeklyDateRange = (startDayOfWeek: number): DateRange<Dayjs> => {
    const normalizedStartDayOfWeek = startDayOfWeek === 7 ? 0 : startDayOfWeek;
    const currentDate = dayjs().startOf("day");
    const currentWeekStart = currentDate.subtract((currentDate.day() - normalizedStartDayOfWeek + 7) % 7, "day");

    return [currentWeekStart.subtract(52, "week"), currentWeekStart.subtract(1, "day")];
};

const getDefaultDailyDateRange = (): DateRange<Dayjs> => {
    const latestCompletedDay = dayjs().startOf("day").subtract(1, "day");
    return [latestCompletedDay.subtract(41, "day"), latestCompletedDay];
};

const formatNumber = (value: number): string => new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(value);
const formatCurrency = (value: number): string => new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2
}).format(value);

const performanceMetrics: { key: WaitroseSkuMetric; label: string; apiValue: string; value: (period: WaitrosePerformancePeriod) => number; format: (value: number | null) => string }[] = [
    { key: "registeredSales", label: "Registered sales", apiValue: "RegisteredSales", value: period => period.registeredSales, format: value => value === null ? "—" : formatCurrency(value) },
    { key: "salesUnits", label: "Sales units", apiValue: "SalesUnits", value: period => period.salesUnits, format: value => value === null ? "—" : formatNumber(value) },
    { key: "reductions", label: "Reductions", apiValue: "Reductions", value: period => period.reductions, format: value => value === null ? "—" : formatCurrency(value) },
    { key: "explainedWastage", label: "Explained wastage", apiValue: "ExplainedWastage", value: period => period.explainedWastage, format: value => value === null ? "—" : formatCurrency(value) },
    { key: "explainedWastageQuality", label: "Quality wastage", apiValue: "ExplainedWastageQuality", value: period => period.explainedWastageQuality, format: value => value === null ? "—" : formatCurrency(value) }
];

const toTabSeparatedValue = (headers: string[], rows: string[][]) =>
    [headers, ...rows]
        .map(row => row.map(value => String(value).replace(/[\t\r\n]/g, " ")).join("\t"))
        .join("\n");

const toCsvContent = (headers: string[], rows: string[][]) =>
    [headers, ...rows]
        .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(","))
        .join("\n");

const sanitizeSpreadsheetText = (value: string) => /^[\t\r\n ]*[=+\-@]/.test(value) ? `'${value}` : value;

const errorMessage = (error: unknown, fallback = "Waitrose EPOS data could not be loaded."): string => {
    if (axios.isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; detail?: string } | undefined;
        return responseData?.message ?? responseData?.detail ?? fallback;
    }
    return fallback;
};

interface EposWaitroseDataViewProps {
    onSelectRetailer: (retailer: string) => void;
    startDayOfWeek?: number;
}

export default function EposWaitroseDataView({ onSelectRetailer, startDayOfWeek = 0 }: EposWaitroseDataViewProps) {
    const [grain, setGrain] = useState<Grain>("weekly");
    const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(() => getDefaultWeeklyDateRange(startDayOfWeek));
    const dateRangeCustomized = useRef(false);
    const [periods, setPeriods] = useState<WaitrosePerformancePeriod[]>([]);
    const [chartMode, setChartMode] = useState<ChartMode>("total");
    const [metricOne, setMetricOne] = useState<WaitroseSkuMetric>("registeredSales");
    const [metricTwo, setMetricTwo] = useState<WaitroseChartMetric>("salesUnits");
    const [expandedMetrics, setExpandedMetrics] = useState<Set<WaitroseSkuMetric>>(new Set());
    const [metricDetails, setMetricDetails] = useState<Partial<Record<WaitroseSkuMetric, WaitroseSkuPerformanceResponse>>>({});
    const [metricDetailLoading, setMetricDetailLoading] = useState<Partial<Record<WaitroseSkuMetric, boolean>>>({});
    const [metricDetailErrors, setMetricDetailErrors] = useState<Partial<Record<WaitroseSkuMetric, string>>>({});
    const [branches, setBranches] = useState<WaitroseStorePerformance[]>([]);
    const [branchCount, setBranchCount] = useState(0);
    const [branchPage, setBranchPage] = useState(0);
    const [branchPageSize, setBranchPageSize] = useState(10);
    const [branchSortBy, setBranchSortBy] = useState<WaitroseStoreSortKey>("registeredSales");
    const [branchSortDirection, setBranchSortDirection] = useState<"asc" | "desc">("desc");
    const [performanceLoading, setPerformanceLoading] = useState(true);
    const [branchLoading, setBranchLoading] = useState(true);
    const [performanceError, setPerformanceError] = useState("");
    const [branchError, setBranchError] = useState("");
    const [storeActionError, setStoreActionError] = useState("");
    const [storeCsvExportLoading, setStoreCsvExportLoading] = useState(false);
    const [unmappedCount, setUnmappedCount] = useState(0);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
    const [selectedProductGroups, setSelectedProductGroups] = useState<string[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
    const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
    const productFilterParams = useMemo<ProductFilterParams>(() => ({
        ...(selectedProductIds.length > 0 ? { productIds: selectedProductIds } : {}),
        ...(selectedBrands.length > 0 ? { brands: selectedBrands } : {}),
        ...(selectedProductGroups.length > 0 ? { productGroups: selectedProductGroups } : {})
    }), [selectedBrands, selectedProductGroups, selectedProductIds]);
    const metricDetailRequestControllers = useRef(new Map<WaitroseSkuMetric, AbortController>());
    const storeCsvExportRequestController = useRef<AbortController | null>(null);

    const cancelStoreCsvExport = useCallback(() => {
        const controller = storeCsvExportRequestController.current;
        if (!controller) return;
        controller.abort();
        storeCsvExportRequestController.current = null;
        setStoreCsvExportLoading(false);
    }, []);

    useEffect(() => {
        if (grain === "weekly" && !dateRangeCustomized.current) {
            setDateRange(getDefaultWeeklyDateRange(startDayOfWeek));
        }
    }, [grain, startDayOfWeek]);

    const fromDate = dateRange[0]?.format("YYYY-MM-DD") ?? "";
    const toDate = dateRange[1]?.format("YYYY-MM-DD") ?? "";
    const hasProductFilters = selectedBrands.length > 0 || selectedProductGroups.length > 0 || selectedProductIds.length > 0;
    const hasNoMatchingProducts = useMemo(() => hasProductFilters && !productOptions.some(product =>
        (selectedBrands.length === 0 || selectedBrands.includes(product.brand)) &&
        (selectedProductGroups.length === 0 || selectedProductGroups.includes(product.groupName ?? "")) &&
        (selectedProductIds.length === 0 || selectedProductIds.includes(product.productId))),
    [hasProductFilters, productOptions, selectedBrands, selectedProductGroups, selectedProductIds]);
    const totalRegisteredSales = periods.reduce((total, period) => total + period.registeredSales, 0);
    const totalSalesUnits = periods.reduce((total, period) => total + period.salesUnits, 0);
    const totalReductions = periods.reduce((total, period) => total + period.reductions, 0);
    const totalWastage = periods.reduce((total, period) => total + period.explainedWastage, 0);
    const totalQualityWastage = periods.reduce((total, period) => total + period.explainedWastageQuality, 0);
    const selectedMetricOne = performanceMetrics.find(metric => metric.key === metricOne)!;
    const selectedMetricTwo = metricTwo === "none"
        ? null
        : performanceMetrics.find(metric => metric.key === metricTwo)!;
    const skuPerformance = metricDetails[metricOne]?.items ?? [];
    const skuLoading = metricDetailLoading[metricOne] ?? false;
    const skuError = metricDetailErrors[metricOne] ?? "";
    const chartSeries = chartMode === "sku"
        ? skuPerformance.map(item => ({
            label: `${item.lineDescription} (${item.lineNumber})`,
            data: periods.map(period => item.values.find(value => value.period === period.period)?.value ?? null),
            yAxisId: "metric-one"
        }))
        : [
            {
                label: selectedMetricOne.label,
                data: periods.map(selectedMetricOne.value),
                yAxisId: "metric-one"
            },
            ...(selectedMetricTwo === null ? [] : [{
                label: selectedMetricTwo.label,
                data: periods.map(selectedMetricTwo.value),
                yAxisId: "metric-two"
            }])
        ];
    const chartYAxes = chartMode === "total" && metricTwo !== "none"
        ? [
            { id: "metric-one", scaleType: "linear" as const, position: "left" as const },
            { id: "metric-two", scaleType: "linear" as const, position: "right" as const }
        ]
        : [{ id: "metric-one", scaleType: "linear" as const, position: "left" as const }];

    const resetMetricDetails = useCallback(() => {
        metricDetailRequestControllers.current.forEach(controller => controller.abort());
        metricDetailRequestControllers.current.clear();
        setExpandedMetrics(new Set());
        setMetricDetails({});
        setMetricDetailLoading({});
        setMetricDetailErrors({});
    }, []);

    const loadMetricDetails = useCallback(async (metricKey: WaitroseSkuMetric, retryOnError = false) => {
        if (!fromDate || !toDate || fromDate > toDate || hasNoMatchingProducts ||
            metricDetails[metricKey] || metricDetailLoading[metricKey] ||
            (!retryOnError && metricDetailErrors[metricKey])) {
            return;
        }

        metricDetailRequestControllers.current.get(metricKey)?.abort();
        const controller = new AbortController();
        metricDetailRequestControllers.current.set(metricKey, controller);
        setMetricDetailLoading(current => ({ ...current, [metricKey]: true }));
        setMetricDetailErrors(current => ({ ...current, [metricKey]: undefined }));
        const metric = performanceMetrics.find(item => item.key === metricKey)!;

        try {
            const response = await axiosInstance.get<WaitroseSkuPerformanceResponse>("/epos/waitrose/performance/details", {
                params: {
                    daily: grain === "daily",
                    fromDate,
                    toDate,
                    metric: metric.apiValue,
                    ...productFilterParams
                },
                paramsSerializer: { indexes: null },
                signal: controller.signal
            });
            if (!controller.signal.aborted) {
                setMetricDetails(current => ({ ...current, [metricKey]: response.data }));
            }
        } catch (requestError) {
            if (!controller.signal.aborted && !axios.isCancel(requestError)) {
                setMetricDetailErrors(current => ({
                    ...current,
                    [metricKey]: errorMessage(requestError, "Waitrose SKU performance could not be loaded.")
                }));
            }
        } finally {
            if (!controller.signal.aborted) {
                setMetricDetailLoading(current => ({ ...current, [metricKey]: false }));
                metricDetailRequestControllers.current.delete(metricKey);
            }
        }
    }, [fromDate, grain, hasNoMatchingProducts, metricDetailErrors, metricDetailLoading, metricDetails, productFilterParams, toDate]);

    const toggleMetric = (metric: WaitroseSkuMetric) => {
        const isExpanded = expandedMetrics.has(metric);
        setExpandedMetrics(current => {
            const next = new Set(current);
            if (isExpanded) next.delete(metric);
            else next.add(metric);
            return next;
        });
        if (!isExpanded) void loadMetricDetails(metric, true);
    };

    const performanceTable = useMemo(() => ({
        headers: ["Metric", ...periods.map(period => dayjs(period.period).format("D MMM YYYY"))],
        rows: performanceMetrics.map(metric => [metric.label, ...periods.map(period => metric.format(metric.value(period)))])
    }), [periods]);

    const storeTable = useMemo(() => ({
        headers: storeColumns.map(column => column.label),
        rows: branches.map(branch => storeColumns.map(column => column.key === "branchName"
            ? sanitizeSpreadsheetText(branch.branchName)
            : column.key === "salesUnits"
                ? formatNumber(branch.salesUnits)
                : formatCurrency(branch[column.key])))
    }), [branches]);

    const copyPerformanceToClipboard = async () => {
        if (!periods.length || performanceLoading) return;
        try {
            await navigator.clipboard.writeText(toTabSeparatedValue(performanceTable.headers, performanceTable.rows));
        } catch (clipboardError) {
            console.error("Failed to copy Waitrose performance to clipboard:", clipboardError);
            setPerformanceError("Performance data could not be copied to the clipboard.");
        }
    };

    const exportPerformanceCsv = () => {
        if (!periods.length || performanceLoading) return;
        const csv = toCsvContent(performanceTable.headers, performanceTable.rows);
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = grain === "daily" ? "waitrose-daily-performance.csv" : "waitrose-weekly-performance.csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const copyStorePerformanceToClipboard = async () => {
        if (!branches.length || branchLoading) return;
        try {
            await navigator.clipboard.writeText(toTabSeparatedValue(storeTable.headers, storeTable.rows));
            setStoreActionError("");
        } catch (clipboardError) {
            console.error("Failed to copy Waitrose store performance to clipboard:", clipboardError);
            setStoreActionError("Store performance could not be copied to the clipboard.");
        }
    };

    const exportStorePerformanceCsv = async () => {
        if (!branches.length || branchLoading || storeCsvExportLoading || !fromDate || !toDate) return;
        storeCsvExportRequestController.current?.abort();
        const controller = new AbortController();
        storeCsvExportRequestController.current = controller;
        const isCurrentExport = () => storeCsvExportRequestController.current === controller && !controller.signal.aborted;
        setStoreActionError("");
        await downloadCsv(
            "/epos/waitrose/store-performance/csv",
            { fromDate, toDate, ...productFilterParams, sortBy: branchSortBy, sortDirection: branchSortDirection },
            "waitrose-store-performance.csv",
            isLoading => { if (isCurrentExport()) setStoreCsvExportLoading(isLoading); },
            () => { if (isCurrentExport()) setStoreActionError("Store performance could not be exported. Please try again."); },
            { paramsSerializer: { indexes: null }, signal: controller.signal }
        );
        if (storeCsvExportRequestController.current === controller) storeCsvExportRequestController.current = null;
    };

    useEffect(() => {
        let active = true;
        void axiosInstance.get<{ count: number }>("/epos/product-mappings/unmapped-count", { params: { retailer: 3 } })
            .then(response => { if (active) setUnmappedCount(response.data.count); })
            .catch(() => { if (active) setUnmappedCount(0); });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        setBranchPage(0);
    }, [fromDate, hasNoMatchingProducts, productFilterParams, toDate]);

    useEffect(() => resetMetricDetails(), [fromDate, grain, hasNoMatchingProducts, productFilterParams, resetMetricDetails, toDate]);

    useEffect(() => () => {
        metricDetailRequestControllers.current.forEach(controller => controller.abort());
        metricDetailRequestControllers.current.clear();
    }, []);

    useEffect(() => {
        if (chartMode === "sku") void loadMetricDetails(metricOne);
    }, [chartMode, loadMetricDetails, metricOne]);

    useEffect(() => {
        cancelStoreCsvExport();
    }, [branchSortBy, branchSortDirection, cancelStoreCsvExport, fromDate, hasNoMatchingProducts, productFilterParams, toDate]);

    useEffect(() => () => cancelStoreCsvExport(), [cancelStoreCsvExport]);

    useEffect(() => {
        if (!fromDate || !toDate || fromDate > toDate) {
            setPerformanceError(fromDate && toDate ? "The start date must be before the end date." : "Select a complete date range.");
            setPeriods([]);
            setPerformanceLoading(false);
            setBranches([]);
            setBranchCount(0);
            setBranchError("");
            setBranchLoading(false);
            return;
        }
        if (hasNoMatchingProducts) {
            setPerformanceError("");
            setPeriods([]);
            setPerformanceLoading(false);
            setBranches([]);
            setBranchCount(0);
            setBranchError("");
            setBranchLoading(false);
            return;
        }

        setPerformanceLoading(true);
        setPerformanceError("");
        const productParams = productFilterParams;
        const performanceController = new AbortController();

        void axiosInstance.get<WaitrosePerformanceResponse>("/epos/waitrose/performance", {
            params: { daily: grain === "daily", fromDate, toDate, ...productParams },
            paramsSerializer: { indexes: null },
            signal: performanceController.signal
        }).then(response => {
            if (!performanceController.signal.aborted) setPeriods(response.data.periods);
        }).catch(requestError => {
            if (!performanceController.signal.aborted && !axios.isCancel(requestError)) {
                setPeriods([]);
                setPerformanceError(errorMessage(requestError, "Waitrose performance could not be loaded."));
            }
        }).finally(() => {
            if (!performanceController.signal.aborted) setPerformanceLoading(false);
        });

        return () => performanceController.abort();
    }, [fromDate, grain, hasNoMatchingProducts, productFilterParams, toDate]);

    useEffect(() => {
        if (!fromDate || !toDate || fromDate > toDate) {
            setBranches([]);
            setBranchCount(0);
            setBranchError("");
            setBranchLoading(false);
            return;
        }
        if (hasNoMatchingProducts) {
            setBranches([]);
            setBranchCount(0);
            setBranchError("");
            setBranchLoading(false);
            return;
        }

        const branchController = new AbortController();
        setBranchLoading(true);
        setBranchError("");
        const productParams = productFilterParams;

        void axiosInstance.get<WaitroseStorePerformancePage>("/epos/waitrose/store-performance", {
            params: {
                fromDate,
                toDate,
                page: branchPage + 1,
                pageSize: branchPageSize,
                sortBy: branchSortBy,
                sortDirection: branchSortDirection,
                ...productParams
            },
            paramsSerializer: { indexes: null },
            signal: branchController.signal
        }).then(response => {
            if (!branchController.signal.aborted) {
                setBranches(response.data.items);
                setBranchCount(response.data.totalCount);
            }
        }).catch(requestError => {
            if (!branchController.signal.aborted && !axios.isCancel(requestError)) {
                setBranches([]);
                setBranchCount(0);
                setBranchError(errorMessage(requestError, "Waitrose branch performance could not be loaded."));
            }
        }).finally(() => {
            if (!branchController.signal.aborted) setBranchLoading(false);
        });

        return () => branchController.abort();
    }, [branchPage, branchPageSize, branchSortBy, branchSortDirection, fromDate, hasNoMatchingProducts, productFilterParams, toDate]);

    const handleGrainChange = (_event: MouseEvent<HTMLElement>, nextGrain: Grain | null) => {
        if (!nextGrain || nextGrain === grain) return;
        setGrain(nextGrain);
        setBranchPage(0);
        setDateRange(nextGrain === "daily" ? getDefaultDailyDateRange() : getDefaultWeeklyDateRange(startDayOfWeek));
    };

    const handleBranchSort = (key: WaitroseStoreSortKey) => {
        setBranchPage(0);
        if (branchSortBy === key) {
            setBranchSortDirection(direction => direction === "asc" ? "desc" : "asc");
        } else {
            setBranchSortBy(key);
            setBranchSortDirection("desc");
        }
    };

    const handleProductFiltersLoaded = (products: ProductOption[]) => setProductOptions(products);

    return (
        <Box sx={{ width: "100%", maxWidth: 1500, minWidth: 0, mx: "auto", py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
            <EposTabs
                unmappedCount={unmappedCount}
                retailer="3"
                onRetailerChange={onSelectRetailer}
            />

            {unmappedCount > 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Unlinked Waitrose products are excluded from performance totals and product filters.
                </Alert>
            )}

            <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 2 }}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm="auto" sx={{ display: "flex", alignItems: "center", alignSelf: "stretch" }}>
                        <ToggleButtonGroup exclusive size="small" value={grain} onChange={handleGrainChange} aria-label="Performance period">
                            <ToggleButton value="weekly">Weekly</ToggleButton>
                            <ToggleButton value="daily">Daily</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={12} lg={5} sx={{ display: "flex", alignItems: "flex-end" }}>
                        <Box sx={{ width: "100%" }}>
                            <CustomDatePicker
                                value={dateRange}
                                onChange={nextRange => {
                                    dateRangeCustomized.current = true;
                                    setDateRange(nextRange);
                                    setBranchPage(0);
                                }}
                                startLabel="From"
                                endLabel="To"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm="auto" sx={{ display: "flex", alignItems: "center", alignSelf: "stretch" }}>
                        <ProductFiltersButton filtersExpanded={filtersExpanded} onToggle={() => setFiltersExpanded(expanded => !expanded)} />
                    </Grid>
                </Grid>
            </Card>

            <ProductFilters
                selectedForecastId={null}
                selectedForecastPeriodId={null}
                selectedBrands={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                selectedProductGroups={selectedProductGroups}
                setSelectedProductGroups={setSelectedProductGroups}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                filtersExpanded={filtersExpanded}
                onToggle={() => setFiltersExpanded(expanded => !expanded)}
                selectedCustomerId={null}
                selectedChannelId={null}
                productsUrl="/epos/mapped-products?retailer=3"
                onProductsLoaded={handleProductFiltersLoaded}
            />

            {performanceError && <Alert severity="error" sx={{ mb: 2 }}>{performanceError}</Alert>}
            {performanceLoading ? (
                <Card variant="outlined" sx={{ mb: 3 }}><Box sx={{ display: "grid", placeItems: "center", minHeight: 160 }}><Typography color="text.secondary">Loading Waitrose performance…</Typography></Box></Card>
            ) : periods.length === 0 ? (
                <Card variant="outlined" sx={{ mb: 3 }}><Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                    <Typography variant="h6">No Waitrose data</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        {hasNoMatchingProducts ? "No Waitrose products match the selected filters." : "Check the date range, retailer imports and product filters."}
                    </Typography>
                </Box></Card>
            ) : (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {[
                            { label: "Registered sales", value: formatCurrency(totalRegisteredSales) },
                            { label: "Sales units", value: formatNumber(totalSalesUnits) },
                            { label: "Reductions", value: formatCurrency(totalReductions) },
                            { label: "Explained wastage", value: formatCurrency(totalWastage) },
                            { label: "Quality wastage", value: formatCurrency(totalQualityWastage) }
                        ].map(metric => (
                            <Grid item xs={12} sm={6} md={4} key={metric.label}>
                                <Card variant="outlined" sx={{ height: "100%", p: { xs: 2, sm: 2.5 } }}>
                                    <Typography color="text.secondary" variant="body2">{metric.label}</Typography>
                                    <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>{metric.value}</Typography>
                                    <Typography color="text.secondary" variant="caption">Selected period</Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2} sx={{ mb: 2 }}>
                            <Typography variant="h6">Performance Trend</Typography>
                            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={chartMode}
                                    onChange={(_event, value: ChartMode | null) => value && setChartMode(value)}
                                    aria-label="Performance chart mode"
                                >
                                    <ToggleButton value="total">Total</ToggleButton>
                                    <ToggleButton value="sku">SKU</ToggleButton>
                                </ToggleButtonGroup>
                                <FormControl size="small" sx={{ minWidth: 142 }}>
                                    <InputLabel id="waitrose-metric-one-label">Metric 1</InputLabel>
                                    <Select
                                        labelId="waitrose-metric-one-label"
                                        label="Metric 1"
                                        value={metricOne}
                                        onChange={event => setMetricOne(event.target.value as WaitroseSkuMetric)}
                                    >
                                        {performanceMetrics.map(metric => <MenuItem key={metric.key} value={metric.key}>{metric.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 142 }} disabled={chartMode === "sku"}>
                                    <InputLabel id="waitrose-metric-two-label">Metric 2</InputLabel>
                                    <Select
                                        labelId="waitrose-metric-two-label"
                                        label="Metric 2"
                                        value={metricTwo}
                                        disabled={chartMode === "sku"}
                                        onChange={event => setMetricTwo(event.target.value as WaitroseChartMetric)}
                                    >
                                        <MenuItem value="none">None</MenuItem>
                                        {performanceMetrics.map(metric => <MenuItem key={metric.key} value={metric.key}>{metric.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Stack>
                        {chartMode === "sku" && metricDetails[metricOne]?.isTruncated && (
                            <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
                                Showing up to {metricDetails[metricOne]?.resultLimit ?? 100} mapped Waitrose product lines ranked by the selected metric over this date range. Use product filters to narrow the results.
                            </Alert>
                        )}
                        {chartMode === "sku" && skuLoading ? (
                            <Box sx={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                <CircularProgress size={22} />
                                <Typography color="text.secondary">Loading SKU performance…</Typography>
                            </Box>
                        ) : chartMode === "sku" && skuError ? (
                            <Alert severity="error">{skuError}</Alert>
                        ) : chartMode === "sku" && skuPerformance.length === 0 ? (
                            <Box sx={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography color="text.secondary">No mapped SKU performance data available.</Typography>
                            </Box>
                        ) : (
                            <EposPerformanceTrendChart
                                periods={periods.map(period => period.period)}
                                series={chartSeries}
                                yAxis={chartYAxes}
                                leftAxis="metric-one"
                                rightAxis={chartMode === "total" && metricTwo !== "none" ? "metric-two" : null}
                            />
                        )}
                    </Card>

                    <Card variant="outlined" sx={{ mb: 3 }}>
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2} sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: 1, borderColor: "divider" }}>
                            <Typography variant="h6">{grain === "weekly" ? "Weekly Performance" : "Daily Performance"}</Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                <Tooltip title="Copy table to clipboard">
                                    <IconButton aria-label="Copy performance table to clipboard" onClick={() => void copyPerformanceToClipboard()} disabled={performanceLoading}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Export CSV">
                                    <IconButton aria-label="Export performance CSV" onClick={exportPerformanceCsv} disabled={performanceLoading}>
                                        <FileDownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                        <TableContainer sx={{ overflowX: "auto" }}>
                            <Table size="small" aria-label={grain === "weekly" ? "Waitrose weekly performance" : "Waitrose daily performance"} sx={{ minWidth: 760, width: "max-content" }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, position: "sticky", left: 0, zIndex: 2, backgroundColor: theme => theme.palette.background.paper }}>Metric</TableCell>
                                        {periods.map(period => (
                                            <TableCell key={period.period} align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                                {dayjs(period.period).format("D MMM YYYY")}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {performanceMetrics.map(metric => {
                                        const isExpanded = expandedMetrics.has(metric.key);
                                        const details = metricDetails[metric.key];
                                        const detailLoading = metricDetailLoading[metric.key];
                                        const detailError = metricDetailErrors[metric.key];

                                        return (
                                            <Fragment key={metric.key}>
                                                <TableRow>
                                                    <TableCell component="th" scope="row" sx={{ fontWeight: 600, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 1, backgroundColor: theme => theme.palette.background.paper }}>
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <IconButton
                                                                size="small"
                                                                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${metric.label}`}
                                                                aria-expanded={isExpanded}
                                                                onClick={() => toggleMetric(metric.key)}
                                                            >
                                                                {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                                            </IconButton>
                                                            {metric.label}
                                                        </Stack>
                                                    </TableCell>
                                                    {periods.map(period => (
                                                        <TableCell key={period.period} align="right" sx={{ whiteSpace: "nowrap" }}>
                                                            {metric.format(metric.value(period))}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                                {isExpanded && detailLoading && (
                                                    <TableRow><TableCell colSpan={periods.length + 1}>
                                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 5 }}>
                                                            <CircularProgress size={18} />
                                                            <Typography color="text.secondary" variant="body2">Loading mapped product lines…</Typography>
                                                        </Stack>
                                                    </TableCell></TableRow>
                                                )}
                                                {isExpanded && !detailLoading && detailError && (
                                                    <TableRow><TableCell colSpan={periods.length + 1}><Alert severity="error">{detailError}</Alert></TableCell></TableRow>
                                                )}
                                                {isExpanded && !detailLoading && !detailError && details?.isTruncated && (
                                                    <TableRow>
                                                        <TableCell colSpan={periods.length + 1}>
                                                            <Alert severity="info">
                                                                Showing up to {details.resultLimit ?? 100} mapped Waitrose product lines ranked by {metric.label.toLowerCase()} over this date range. Use product filters to narrow the results.
                                                            </Alert>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {isExpanded && !detailLoading && !detailError && details?.items.map(item => (
                                                    <TableRow key={`${metric.key}-${item.lineNumber}-${item.lineDescription}`}>
                                                        <TableCell component="th" scope="row" sx={{ pl: 6, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 1, backgroundColor: theme => theme.palette.background.paper }}>
                                                            {item.lineDescription} ({item.lineNumber})
                                                        </TableCell>
                                                        {periods.map(period => (
                                                            <TableCell key={period.period} align="right" sx={{ whiteSpace: "nowrap" }}>
                                                                {metric.format(item.values.find(value => value.period === period.period)?.value ?? null)}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                                {isExpanded && !detailLoading && !detailError && details?.items.length === 0 && (
                                                    <TableRow><TableCell colSpan={periods.length + 1} sx={{ pl: 6 }}>
                                                        No mapped product lines are available for this metric and date range.
                                                    </TableCell></TableRow>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </>
            )}

            <Card variant="outlined" sx={{ mb: 3 }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2} sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: 1, borderColor: "divider" }}>
                    <Typography variant="h6">Store Performance</Typography>
                    {!branchLoading && !branchError && branches.length > 0 && (
                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                            <Tooltip title="Copy table to clipboard">
                                <IconButton aria-label="Copy store performance table to clipboard" onClick={() => void copyStorePerformanceToClipboard()} disabled={storeCsvExportLoading}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={storeCsvExportLoading ? "Exporting CSV…" : "Export CSV"}>
                                <span>
                                    <IconButton aria-label="Export store performance CSV" onClick={() => void exportStorePerformanceCsv()} disabled={storeCsvExportLoading} aria-busy={storeCsvExportLoading}>
                                        {storeCsvExportLoading ? <CircularProgress size={20} /> : <FileDownloadIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>
                {storeActionError && <Alert severity="error" sx={{ m: 2, mb: 0 }}>{storeActionError}</Alert>}
                {branchLoading ? (
                    <Box sx={{ display: "grid", placeItems: "center", minHeight: 120 }}><Typography color="text.secondary">Loading branch performance…</Typography></Box>
                ) : branchError ? (
                    <Alert severity="error" sx={{ m: 2 }}>{branchError}</Alert>
                ) : branches.length === 0 ? (
                    <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                        <Typography variant="h6">No branch data for these filters</Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>Try changing the date range or product filters.</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ overflowX: "auto" }}>
                            <Table size="small" aria-label="Waitrose store performance" sx={{ minWidth: 1100 }}>
                                <TableHead><TableRow>{storeColumns.map(column => (
                                    <TableCell
                                        key={column.key}
                                        align={column.align ?? "left"}
                                        sortDirection={branchSortBy === column.key ? branchSortDirection : false}
                                        sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                                    >
                                        <TableSortLabel
                                            active={branchSortBy === column.key}
                                            direction={branchSortBy === column.key ? branchSortDirection : "asc"}
                                            onClick={() => handleBranchSort(column.key)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}</TableRow></TableHead>
                                <TableBody>{branches.map(branch => (
                                    <TableRow hover key={branch.branchName}>
                                        {storeColumns.map(column => (
                                            <TableCell key={column.key} align={column.align ?? "left"} sx={{ whiteSpace: "nowrap" }}>
                                                {column.key === "branchName"
                                                    ? branch.branchName
                                                    : column.key === "salesUnits"
                                                        ? formatNumber(branch.salesUnits)
                                                        : formatCurrency(branch[column.key])}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}</TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={branchCount}
                            page={branchPage}
                            rowsPerPage={branchPageSize}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            onPageChange={(_event, nextPage) => setBranchPage(nextPage)}
                            onRowsPerPageChange={event => { setBranchPageSize(Number(event.target.value)); setBranchPage(0); }}
                        />
                    </>
                )}
            </Card>

        </Box>
    );
}
